/**
 * Ask before doing something the person cannot take back.
 *
 * Built on `Modal`, so it inherits the focus trap, Escape-to-close and the
 * click-outside guard. Every one of those routes is a *cancel*: the only way to
 * the confirming action is the button, which is what makes the dialog safe to put
 * in front of a destructive step.
 */

import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { Modal } from "@/components/ui/Modal";
import { cn, toTitleCase } from "@/lib/format";

export function ConfirmDialog({
  open,
  title,
  description,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  busy = false,
  icon,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description?: string;
  /** Anything the person needs to read before deciding. */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "destructive";
  /** Keeps the dialog up, and both buttons out of reach, while the work runs. */
  busy?: boolean;
  icon?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Modal puts initial focus on the first control, which here is Cancel. That is
  // deliberate: Enter on a dialog nobody has read yet should back out, not go
  // through with the thing being asked about.
  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onCancel}
      title={title}
      width="sm"
      footer={
        <>
          <button
            className="btn btn-outline"
            onClick={onCancel}
            disabled={busy}
          >
            {toTitleCase(cancelLabel)}
          </button>
          <button
            className={cn(
              "btn",
              tone === "destructive" ? "btn-danger" : "btn-primary",
            )}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : null}
            {toTitleCase(confirmLabel)}
          </button>
        </>
      }
    >
      <div className="flex gap-3">
        {icon ? (
          <span
            className={cn(
              "grid size-9 shrink-0 place-items-center rounded-full",
              tone === "destructive"
                ? "bg-destructive/10 text-destructive"
                : "bg-muted text-muted-foreground",
            )}
          >
            {icon}
          </span>
        ) : null}
        <div className="min-w-0 space-y-2">
          {description ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          ) : null}
          {body}
        </div>
      </div>
    </Modal>
  );
}
